/*
 **             Pane for running existing forms for any object
 **
 */

import * as UI from 'solid-ui'
import { authn } from 'solid-logic'
import * as $rdf from 'rdflib'
import type { DataBrowserContext, PaneDefinition } from 'pane-registry'
import type { RenderEnvironment } from 'pane-registry'
import type { NamedNode, Statement } from 'rdflib'
import './formPane.css'
const ns = UI.ns

type WorkspaceSelectionDetails = {
  noun: string
  appPathSegment: string
}

function isNamedNode (term: { termType?: string } | null | undefined): term is NamedNode {
  return term?.termType === 'NamedNode'
}

/* The following helper was generated by AI GPT-5.4 Model  */
/* Prompt: Add a compatibility shim in the form pane for mixed `ui:Group` plus field typing. */
function normalizeAmbiguousFieldTypes (store: typeof $rdf.graph extends () => infer T ? T : never, form: NamedNode): void {
  const formDoc = form.doc ? form.doc() : undefined
  const types = store.each(form, ns.rdf('type'), undefined, formDoc).filter(isNamedNode)
  const hasGroupType = types.some(type => type.sameTerm(ns.ui('Group')))
  const hasOtherFieldType = types.some(type => !type.sameTerm(ns.ui('Group')) && !type.sameTerm(ns.ui('Form')))
  const hasProperty = !!store.any(form, ns.ui('property'), undefined, formDoc)
  const partsList = store.any(form, ns.ui('parts'), undefined, formDoc) as { elements?: unknown[] } | null
  const hasParts = !!partsList?.elements?.length || store.each(form, ns.ui('part'), undefined, formDoc).length > 0

  // Some legacy forms mark a leaf field as both ui:Group and a concrete input type.
  // solid-ui may then choose the Group renderer and produce an empty nested box.
  if (hasGroupType && hasOtherFieldType && hasProperty && !hasParts) {
    store.removeMany(form, ns.rdf('type'), ns.ui('Group'), formDoc)
  }

  const listParts = partsList?.elements ?? []
  for (const part of listParts) {
    if (isNamedNode(part as { termType?: string })) {
      normalizeAmbiguousFieldTypes(store, part as NamedNode)
    }
  }

  const unorderedParts = store.each(form, ns.ui('part'), undefined, formDoc)
  for (const part of unorderedParts) {
    if (isNamedNode(part)) {
      normalizeAmbiguousFieldTypes(store, part)
    }
  }
}

function tagMobileTextareaRows (renderedForm: HTMLElement): void {
  const textareas = Array.from(renderedForm.querySelectorAll('textarea'))

  for (const textarea of textareas) {
    let row: HTMLDivElement | null = textarea.parentElement as HTMLDivElement | null

    while (row) {
      const firstChild = row.firstElementChild as HTMLElement | null
      const hasLabelColumn = !!firstChild && (
        firstChild.classList.contains('formFieldName') ||
        firstChild.querySelector('a') !== null
      )

      if (hasLabelColumn && row.children.length >= 2) {
        row.classList.add('formPane__mobileTextareaRow')
        firstChild.classList.add('formPane__mobileTextareaLabel')
        const valueColumn = row.children[1] as HTMLElement
        valueColumn.classList.add('formPane__mobileTextareaValue')
        break
      }

      row = row.parentElement as HTMLDivElement | null
    }
  }
}

export const formPane: PaneDefinition = {
  icon: UI.icons.iconBase + 'noun_122196.svg',

  name: 'form',

  audience: [ns.solid('PowerUser')],

  // Does the subject deserve this pane?
  label: function (subject: NamedNode, _context: DataBrowserContext): string | null {
    const n = UI.widgets.formsFor(subject).length
    UI.log.debug('Form pane: forms for ' + subject + ': ' + n)
    if (!n) return null
    return '' + n + ' forms'
  },

  render: function (subject: NamedNode, context: DataBrowserContext): HTMLDivElement {
    const kb = context.session.store
    const dom = context.dom
    const box = dom.createElement('div')
    box.setAttribute('class', 'formPane')

    function applyEnvironmentAttributes (element: HTMLDivElement): void {
      const environment = (context.environment ?? {}) as Partial<RenderEnvironment>
      element.dataset.layout = environment.layout ?? 'desktop'
    }

    applyEnvironmentAttributes(box)

    const mention = function (
      message: string,
      modifier: 'info' | 'error' = 'info'
    ): HTMLParagraphElement {
      const pre = dom.createElement('p')
      pre.className = `formPane__message formPane__message--${modifier}`
      box.appendChild(pre).textContent = message
      return pre
    }

    const complain = function (message: string): HTMLParagraphElement {
      return mention(message, 'error')
    }

    const complainIfBad = function (ok: boolean, body: string): void {
      if (ok) {
        // setModifiedDate(store, kb, store);
        // rerender(box);   // Deleted forms at the moment
      } else complain('Sorry, failed to save your change:\n' + body)
    }

    // The question of where to store this data about subject
    // This in general needs a whole lot more thought
    // and it connects to the discoverbility through links

    // const t = kb.findTypeURIs(subject)

    const me = authn.currentUser()

    if (!me) {
      mention(
        'You are not logged in. If you log in and have ' +
          'workspaces then you would be able to select workspace in which ' +
          'to put this new information'
      )
    } else {
      const ws = kb.each(me, ns.ui('workspace'))
      if (ws.length === 0) {
        mention(
          'You don\'t seem to have any workspaces defined.  ' +
            'A workspace is a place on the web (http://..) or in ' +
            'the file system (file:///) to store application data.\n'
        )
      } else {
        // @@
      }
    }

    // Render forms using a given store

    const renderFormsFor = function (storeNode: NamedNode, targetSubject: NamedNode): void {
      kb.fetcher.nowOrWhenFetched(
        storeNode.uri,
        targetSubject as unknown as Parameters<typeof kb.fetcher.nowOrWhenFetched>[1],
        function (ok, body) {
          if (!ok) return complain('Cannot load store ' + storeNode.uri + ': ' + body)

          //              Render the forms

          const forms = UI.widgets.formsFor(targetSubject) as NamedNode[]

          // complain('Form for editing this form:');
          for (const form of forms) {
            normalizeAmbiguousFieldTypes(kb as never, form)

            const heading = dom.createElement('h4')
            heading.classList.add('formPane__heading')
            box.appendChild(heading)
            /* The edit Form is not working in the local environment. it does not find
            the ui FormForm ontology. Need to research further and check in production. */
            if (form.uri) {
              const formStore = $rdf.Util.uri.document(form.uri)
              if (formStore.uri !== form.uri) {
                const editButton = box.appendChild(
                  UI.widgets.editFormButton(
                    dom,
                    box,
                    form,
                    formStore,
                    complainIfBad
                  )
                )
                editButton.classList.add('formPane__editButton')
              }
            }

            const anchor = dom.createElement('a')
            anchor.classList.add('formPane__headingLink')
            anchor.setAttribute('href', form.uri)
            heading.insertBefore(anchor, heading.firstChild)
            anchor.textContent = UI.utils.label(form, true)

            /*  Keep tis as a reminder to let a New one have its URI given by user
          mention("Where will this information be stored?")
          const ele = dom.createElement('input');
          box.appendChild(ele);
          ele.setAttribute('type', 'text');
          ele.setAttribute('size', '72');
          ele.setAttribute('maxlength', '1024');
          ele.setAttribute('style', 'font-size: 80%; color:#222;');
          ele.value = store.uri
          */

            UI.widgets.appendForm(
              dom,
              box,
              {},
              targetSubject,
              form,
              storeNode,
              complainIfBad
            )
            tagMobileTextareaRows(box)
          }
        }
      ) // end: when store loded
    } // renderFormsFor

    // Figure out what store

    // Which places are editable and have stuff about the subject?

    let targetStore: NamedNode | null = null

    // 1. The document URI of the subject itself
    const docuri = $rdf.Util.uri.docpart(subject.uri)
    if (kb.updater.editable(docuri, kb)) {
      targetStore = subject.doc()
    } // an editable data file with hash

    const annotationStore = kb.any(kb.sym(docuri), ns.link('annotationStore'))
    if (!targetStore && isNamedNode(annotationStore)) {
      targetStore = annotationStore
    }

    // 2. where stuff is already stored
    if (!targetStore) {
      const docs = new Map<string, number>()
      kb.statementsMatching(subject).forEach(function (st: Statement) {
        if (st.why.value) {
          docs.set(st.why.value, 1)
        }
      })
      kb
        .statementsMatching(undefined, undefined, subject)
        .forEach(function (st: Statement) {
          if (st.why.value) {
            docs.set(st.why.value, 2)
          }
        })
      const docList = Array.from(docs.entries()).sort(function ([uriA, scoreA], [uriB, scoreB]) {
        return scoreA - scoreB || uriA.localeCompare(uriB)
      })
      for (const [uri] of docList) {
        if (uri && kb.updater.editable(uri, kb)) {
          targetStore = kb.sym(uri)
          break
        }
      }
    }

    // 3. In a workspace store
    const date = '2014' // @@@@@@@@@@@@ pass as parameter

    if (targetStore) {
      // mention("@@ Ok, we have a store <" + store.uri + ">.");
      renderFormsFor(targetStore, subject)
    } else {
      complain('No suitable store is known, to edit <' + subject.uri + '>.')
      const workspaceDetails: WorkspaceSelectionDetails = {
        noun: 'form',
        appPathSegment: 'form'
      }
      const foobarbaz = UI.login.selectWorkspace(dom, workspaceDetails, function (workspaceUri: string | null) {
        const workspace = workspaceUri ? kb.sym(workspaceUri) : null
        if (!workspace) {
          complain('Workspace selection was cancelled.')
          return
        }
        mention('Workspace selected OK: ' + workspace.uri)

        const activities = kb.each(undefined, ns.space('workspace'), workspace).filter(isNamedNode)
        for (let j = 0; j < activities.length; j++) {
          const act = activities[j]

          const subjectDoc2 = kb.any(act, ns.space('store'))
          const start = kb.any(act, ns.cal('dtstart'))?.value
          const end = kb.any(act, ns.cal('dtend'))?.value
          if (isNamedNode(subjectDoc2) && start && end && start <= date && end > date) {
            renderFormsFor(subjectDoc2, subject)
            break
          } else {
            complain('Note no suitable annotation store in activity: ' + act)
          }
        }
      })
      box.appendChild(foobarbaz)
    }

    return box
  }
}
